import React, { useLayoutEffect, useRef, useState } from 'react';
import { useClickOutside } from 'hooks/useClickOutside';

interface RowsPerPageSelectProps {
  value: number;
  onChange: (value: number) => void;
  options?: number[];
}

/**
 * Rows-per-page dropdown that always opens upward with a controlled popover,
 * instead of a native <select> whose option list the browser would otherwise
 * open downward and get clipped, since this control always sits at the
 * bottom of the table it paginates.
 */
const RowsPerPageSelect: React.FC<RowsPerPageSelectProps> = ({
  value,
  onChange,
  options = [10, 50, 100],
}) => {
  const [open, setOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);

  // Rough list height (each option row ~36px) plus a little breathing room —
  // enough to decide direction before the list itself has painted.
  const estimatedListHeight = options.length * 36 + 16;

  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const { bottom } = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - bottom;
    setOpenUpward(spaceBelow < estimatedListHeight);
  }, [open, estimatedListHeight]);

  return (
    <div className="relative w-fit" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between gap-3 min-w-[72px] pl-3 pr-2 py-2 rounded-lg bg-lightblue-100 text-sm text-grease-100 focus:outline-none"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{value}</span>
        <i
          className={`st-arrow-right text-xs transition-transform ${
            open ? '-rotate-90' : 'rotate-90'
          }`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <ul
          className="absolute left-0 bottom-full mb-1 z-20 w-full min-w-[72px] bg-white rounded-lg shadow-lg border border-lightgray-100 overflow-hidden"
          role="listbox"
        >
          {options.map((opt) => (
            <li key={opt}>
              <button
                type="button"
                role="option"
                aria-selected={opt === value}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-1 text-sm hover:bg-lightblue-100 transition-colors ${
                  opt === value ? 'bg-lightblue-100 text-royalblue-100 font-medium' : 'text-grease-100'
                }`}
              >
                {opt}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RowsPerPageSelect;
